/**
 * MissingItemList - Display items that are missing or need attention
 *
 * Shows documents, certifications, or other items that are required
 * but not present or need action.
 */

import React from 'react';
import { AlertCircle, FileQuestion, Link2 } from 'lucide-react';
import type { MissingItem } from '../../types';
import './MissingItemList.css';

// Re-export from types for backwards compatibility
export type { MissingItem } from '../../types';

export interface MissingItemListProps {
  /** List of missing items */
  items: MissingItem[];

  /** Optional title for the list */
  title?: string;

  /** Callback when an entity link is clicked */
  onEntityClick?: (entityId: string) => void;

  /** @deprecated Legal citations now shown in summary section */
  onLegalClick?: (legalRefId: string) => void;

  /** Additional CSS class */
  className?: string;
}

export const MissingItemList: React.FC<MissingItemListProps> = ({
  items,
  title = 'Fehlende Dokumente',
  onEntityClick,
  onLegalClick: _onLegalClick,
  className = '',
}) => {
  if (items.length === 0) {
    return null;
  }

  const formatDate = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className={`missing-items ${className}`}>
      {title && (
        <h4 className="missing-items__title">
          <AlertCircle className="missing-items__title-icon" aria-hidden="true" />
          {title}
        </h4>
      )}
      <ol className="missing-items__list">
        {items.map((item, index) => (
          <li
            key={index}
            className={`missing-items__item missing-items__item--${item.priority || 'warning'}`}
          >
            <div className="missing-items__header">
              <FileQuestion className="missing-items__icon" aria-hidden="true" />
              <span className="missing-items__label">{item.label}</span>
            </div>
            <div className="missing-items__details">
              <span className="missing-items__reason">{item.reason}</span>
              {item.dueDate && (
                <span className="missing-items__due">Frist: {formatDate(item.dueDate)}</span>
              )}
            </div>
            {item.linkedEntityName && item.linkedEntityId && (
              <div className="missing-items__links">
                <button
                  type="button"
                  className="missing-items__entity-link"
                  onClick={() => onEntityClick?.(item.linkedEntityId!)}
                >
                  <Link2 className="missing-items__link-icon" aria-hidden="true" />
                  {item.linkedEntityName}
                </button>
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

export default MissingItemList;
